'use client'

import { useRef, useEffect, useContext, useState } from 'react'
import { type FoundationError, PlayerInterface } from '@foundation-player/loader'
import { createPlayerUI } from '@foundation-player/ui'
import { ConsentContext } from '@/context/ConsentContext'
import { debug } from '@/utils/common'
import { type VideoPlayerProps } from '@/types/video'
import { createLoaderConfig, createUiConfig, createMediaConfig } from './config'
import styles from './VideoPlayer.module.scss'
import { MetaDataContext } from '@/context/MetadataContext'
import { Image } from '@/components/Image'

// Falsche Stream URL

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $AC: any
  }
}

const VideoPlayer = ({
  props,
  providePlayer,
}: {
  props: VideoPlayerProps
  providePlayer?: (player: PlayerInterface) => void
}) => {
  const consentContext = useContext(ConsentContext)
  const metaData = useContext(MetaDataContext)
  const [acReady, setAcReady] = useState(false)
  const [player, setPlayer] = useState<PlayerInterface | undefined>(undefined)

  const elem = useRef<HTMLDivElement>(null)

  async function createPlayerUIAndLoadMedia() {
    try {
      if (elem.current && !player) {
        elem.current.innerHTML = ''

        const loaderConfig = createLoaderConfig({
          videoMode: props.mode,
          isPurMode: consentContext.status === 'purMode',
          isPreview: metaData?.model.isPreview,
          consentString: consentContext.tcString,
        })
        const uiConfig = createUiConfig(props.poster ? new URL(props.poster).pathname : '')
        const mediaConig = createMediaConfig(props)

        debug('videoplayer loaderConfig', loaderConfig)
        debug('videoplayer mediaConfig', mediaConig)

        const pl = await createPlayerUI(elem.current, loaderConfig, uiConfig)

        setPlayer(pl)
        if (providePlayer) providePlayer(pl)

        pl.loadMedia(mediaConig).catch((error: FoundationError) => {
          pl.destroy(error)
        })
      }
    } catch (e) {
      console.log('Error:', e)
    }
  }

  // test
  useEffect(() => {
    if (player) {
      player.loadMedia(createMediaConfig(props)).catch((error: FoundationError) => {
        player.destroy(error)
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props])

  useEffect(() => {
    if (!player && consentContext.consentReady && (consentContext.status === 'purMode' || acReady))
      createPlayerUIAndLoadMedia()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acReady, consentContext])

  useEffect(() => {
    let timeout: undefined | number = undefined

    const listener = () => {
      clearTimeout(timeout)
      setAcReady(true)
    }

    if (window.$AC && typeof window.$AC.playerInitReady === 'function' && window.$AC.playerInitReady()) {
      setAcReady(true)
    } else {
      window.addEventListener('AC_player_start', listener)
      // fallback
      timeout = window.setTimeout(function () {
        window.removeEventListener('AC_player_start', listener)
        setAcReady(true)
      }, 3000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={styles.wrapper}>
      {props.poster && <Image src={props.poster} alt="Video poster" />}
      <div className={styles.container} ref={elem}>
        <video preload="none" width="100%" poster={props.poster} controls>
          {props.source.progressive && <source src={props.source.progressive} id="mp4" type="video/mp4" />}
          {props.source.hls && <source src={props.source.hls} id="m3u8" type="application/vnd.apple.mpegURL" />}
          <track default kind="captions" srcLang="de" />
        </video>
      </div>
    </div>
  )
}

export default VideoPlayer
